import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Button,
	Grid,
	Typography,
	Paper,
	CircularProgress,
	Card,
	CardContent,
	Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import { ErrorModal, useToken, getMomentTz } from "@dexteel/mesf-core";
import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import { AssetQCBD } from "@/pages/asset-details/models/AssetQCBD";
import {
	removeQCBDFromAsset,
	updateAssetQCBDComments,
} from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import TextField from "@material-ui/core/TextField";
import { SimpleTextAreaControl } from "@/components/controls/SimpleSelectorControl";

const moment = getMomentTz();

const commonCardStyles = {
	backgroundColor: "#f8f9fa",
	borderRadius: "6px",
	border: "1px solid #e9ecef",
	marginBottom: "8px",
	display: "flex",
};

const useStyles = makeStyles((theme) => ({
	propertyCard: {
		padding: "8px 16px",
		height: "60px",
		flexDirection: "column",
		justifyContent: "flex-start",
		...commonCardStyles,
	},
	propertyCardFlexibleHeight: {
		padding: "8px 16px",
		height: "100%",
		boxSizing: "border-box",
		flexDirection: "column",
		justifyContent: "flex-start",
		...commonCardStyles,
	},
	propertyLabel: {
		fontSize: "10px",
		fontWeight: 600,
		color: "#6c757d",
		textTransform: "uppercase",
		letterSpacing: "0.5px",
		marginBottom: "2px",
	},
	propertyValue: {
		fontSize: "13px",
		color: "#212529",
		fontWeight: 500,
		wordBreak: "break-word",
		lineHeight: "1.3",
		flex: 1,
		display: "flex",
		alignItems: "center",
		overflow: "hidden",
	},
	deleteButton: {
		backgroundColor: "#dc3545",
		color: "white",
		minWidth: "100px",
		"&:hover": {
			backgroundColor: "#c82333",
		},
	},
	modalTitle: {
		fontSize: "24px",
		fontWeight: 600,
		color: "#212529",
		textAlign: "center",
	},
	assetInfoRow: {
		marginBottom: "12px",
	},
	assetNameCard: {
		backgroundColor: "#e3f2fd",
		padding: "8px 16px",
		borderRadius: "6px",
		border: "1px solid #bbdefb",
		height: "100%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	assetNameText: {
		fontSize: "13px",
		fontWeight: 600,
		color: "#1976d2",
	},
	commentsSection: {
		marginTop: "12px",
		paddingTop: "12px",
		borderTop: "1px solid #e9ecef",
	},
	commentsLabel: {
		fontSize: "13px",
		fontWeight: 600,
		color: "#495057",
		marginBottom: "8px",
	},
	messageTitle: {
		fontSize: 14,
		fontWeight: "bold",
		color: "#0000008A",
	},
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiDialogContent-dividers": {
			padding: 0,
		},
	},
	comments: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "1px solid #3F51B5",
		},
	},
}));

interface Props {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	rowSelected: AssetQCBD | null;
	searchAssetQCBD: Function;
}

export const QCBDPropertiesModal = ({
	show,
	onHide,
	rowSelected,
	searchAssetQCBD,
}: Props) => {
	const { getUserName } = useToken();
	const classes = useStyles();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [comments, setComments] = useState("");
	const [userInput, setUserInput] = useState("");
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

	const {
		state: { assetSearchId },
	} = useAssetDetailsContext();

	useEffect(() => {
		if (rowSelected && show) {
			setComments(rowSelected.Comments || "");
			setUserInput("");
			setIsSubmitLoading(false);
		}
	}, [show, rowSelected]);

	const appendComment = (newComment: string) => {
		const now = moment(new Date()).format("MM/DD/YYYY HH:mm");
		let newValue = "";

		if (
			comments === "" ||
			comments === null ||
			comments === "null" ||
			comments === "undefined" ||
			comments === undefined
		) {
			newValue = getUserName() + " - " + now + " - " + newComment;
		} else {
			newValue =
				comments + "\n" + getUserName() + " - " + now + " - " + newComment;
		}

		if (newComment !== "") {
			setUserInput("");
			setComments(newValue);
		}
	};

	const addNewComment = () => {
		appendComment(userInput);
	};

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			appendComment(userInput);
		}
	};

	const changeComments = (value: string) => {
		setComments(value);
	};

	const handleSave = async () => {
		setIsSubmitLoading(true);
		const res = await updateAssetQCBDComments(
			rowSelected?.AssetId,
			rowSelected?.QCBDRecordId as number,
			rowSelected?.QCBDTypeCode as string,
			comments,
		);
		if (res.ok) {
			searchAssetQCBD();
			setIsSubmitLoading(false);
			onHide(true);
		} else {
			setError(res.message);
			setIsSubmitLoading(false);
		}
	};

	const handleDelete = async () => {
		setIsSubmitLoading(true);
		const res = await removeQCBDFromAsset(rowSelected?.QCBDRecordId as number);
		if (res.ok) {
			searchAssetQCBD();
			setIsSubmitLoading(false);
			setDeleteConfirmOpen(false);
			onHide(true);
		} else {
			setError(res.message);
			setIsSubmitLoading(false);
		}
	};

	if (!rowSelected) return null;

	return (
		<>
			<Dialog
				open={show}
				onClose={() => onHide(false)}
				maxWidth="md"
				fullWidth
				className={classes.root}
				PaperProps={{
					style: {
						borderRadius: "8px",
						height: "78vh",
						maxHeight: "80vh",
						width: "100%",
						margin: "16px",
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					},
				}}
			>
				<DialogContent
					style={{
						padding: "20px 24px 0px",
						overflowY: "auto",
						flex: "1 1 auto",
						minHeight: 0,
						height: "calc(78vh - 69px)",
						maxHeight: "calc(78vh - 69px)",
					}}
				>
					<Typography className={classes.modalTitle} gutterBottom>
						{rowSelected.AssetName || "Unknown Asset"}
					</Typography>
					<Divider style={{ marginBottom: 10 }} />

					<Grid container spacing={2} className={classes.assetInfoRow}>
						<Grid item xs={10}>
							<Paper className={classes.propertyCard} elevation={0}>
								<Typography className={classes.propertyLabel}>
									DESCRIPTION
								</Typography>
								<Typography className={classes.propertyValue}>
									{rowSelected?.Description || "N/A"}
								</Typography>
							</Paper>
						</Grid>
						<Grid item xs={2}>
							<Paper className={classes.propertyCard} elevation={0}>
								<Typography className={classes.propertyLabel}>TYPE</Typography>
								<Typography className={classes.propertyValue}>
									{rowSelected?.Type || "N/A"}
								</Typography>
							</Paper>
						</Grid>
						<Grid item xs={12}>
							<Paper className={classes.propertyCard} elevation={0}>
								<Typography className={classes.propertyLabel}>
									FILE NAME
								</Typography>
								<Typography className={classes.propertyValue}>
									{rowSelected?.Filename}
								</Typography>
							</Paper>
						</Grid>
					</Grid>

					<Grid container spacing={1}>
						<Grid item xs={12}>
							<div className={classes.commentsSection}>
								<Typography className={classes.commentsLabel}>
									Comments
								</Typography>
								<Card
									variant="outlined"
									style={{ backgroundColor: "#E9F2F8" }}
									className={classes.root}
								>
									<CardContent style={{ padding: "8px" }}>
										<Typography className={classes.messageTitle}>
											Comments:
										</Typography>
										<SimpleTextAreaControl
											value={comments || ""}
											onChange={changeComments}
											disabled
											showColor={false}
											styleText={{
												height: "70px",
												border: "none",
												backgroundColor: "#E9F2F8",
												fontSize: 15,
												padding: "4px 8px",
											}}
										/>
									</CardContent>
								</Card>
								<Grid
									container
									spacing={1}
									justifyContent="center"
									alignItems="center"
									style={{ marginTop: "8px" }}
								>
									<Grid item md={9} xs={12}>
										<TextField
											variant="outlined"
											margin="dense"
											multiline
											maxRows={3}
											value={userInput}
											onChange={(e) => setUserInput(e.target.value)}
											label="Comment"
											fullWidth
											onKeyDown={onKeyDown}
											autoComplete="off"
											className={classes.comments}
											size="small"
										/>
									</Grid>
									<Grid item md={3} xs={12}>
										<Button
											fullWidth
											variant="contained"
											color="primary"
											onClick={addNewComment}
											style={{ height: "38px", marginTop: 2 }}
										>
											ADD COMMENT
										</Button>
									</Grid>
								</Grid>
							</div>
						</Grid>
					</Grid>
				</DialogContent>

				<Divider />

				<DialogActions
					style={{
						padding: "16px 24px",
						justifyContent: "space-between",
						alignItems: "center",
						minHeight: "68px",
						height: "68px",
						flexShrink: 0,
					}}
				>
					<Button
						variant="contained"
						className={classes.deleteButton}
						startIcon={<DeleteIcon />}
						onClick={() => setDeleteConfirmOpen(true)}
						disabled={isSubmitLoading}
					>
						REMOVE FROM ASSET
					</Button>

					<div style={{ display: "flex", gap: "8px" }}>
						<Button
							variant="contained"
							color="default"
							onClick={() => onHide(false)}
							disabled={isSubmitLoading}
							style={{ minWidth: "225px" }}
						>
							CANCEL
						</Button>
						<Button
							startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
							disabled={isSubmitLoading}
							variant="contained"
							color="primary"
							onClick={handleSave}
							style={{ minWidth: "225px" }}
						>
							SAVE
						</Button>
					</div>
				</DialogActions>
			</Dialog>

			<Dialog
				open={deleteConfirmOpen}
				onClose={() => setDeleteConfirmOpen(false)}
				maxWidth="sm"
			>
				<DialogTitle>Confirm Remove</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to remove this QCBD file from the asset?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
					<Button
						onClick={handleDelete}
						color="secondary"
						disabled={isSubmitLoading}
						startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
						variant="contained"
					>
						Remove
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
